/**
 * In-app purchase in the iOS app (KAN-10).
 *
 * Typed wrapper around the app-local `StoreKit` plugin
 * (apps/mobile/ios/App/App/StoreKitPlugin.swift), which speaks StoreKit 2.
 *
 * The API decides, not the device. Post every transaction's `jws` to
 * `/billing/apple/transactions` and call `finishTransaction()` only when the
 * answer says `finish`. The plugin never finishes a transaction by itself, so
 * one the server has not recorded yet comes back at the next launch.
 *
 * `onTransactionUpdated()` delivers renewals, Ask to Buy approvals and the
 * unfinished transactions of earlier runs. Its first listener is lost when iOS
 * reloads the web view, so read `currentEntitlements()` on every boot as well.
 *
 * The web code reaches installed apps over the air, including shells built
 * before this plugin existed. `isStoreKitAvailable()` is the feature check;
 * every other function rejects with code `UNAVAILABLE` when it is false.
 * Other rejection codes from the shell: `BAD_ARGS`, `UNKNOWN_PRODUCT`,
 * `PRODUCTS_FAILED`, `PURCHASE_FAILED`, `CANCELLED` (restore sign-in
 * dismissed), `RESTORE_FAILED`, `MANAGE_FAILED`.
 */
import { Capacitor, CapacitorException, ExceptionCode, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

import { getNativePlatform } from './platform';

export interface StoreKitProduct {
  id: string;
  displayName: string;
  description: string;
  displayPrice: string;
  price: number;
  currency: string;
  periodUnit?: 'day' | 'week' | 'month' | 'year';
  periodValue?: number;
}

export interface StoreKitTransaction {
  jws: string;
  transactionId: string;
  originalTransactionId: string;
  productId: string;
  appAccountToken?: string;
  expiresAt?: number;
  revokedAt?: number;
}

export type StoreKitPurchaseOutcome =
  | { status: 'success'; transaction: StoreKitTransaction }
  | { status: 'cancelled' }
  | { status: 'pending' };

type PeriodUnit = NonNullable<StoreKitProduct['periodUnit']>;

const PERIOD_UNITS: readonly PeriodUnit[] = ['day', 'week', 'month', 'year'];

/** A product as the shell sends it; the unit is checked before it goes further. */
type NativeProduct = Omit<StoreKitProduct, 'periodUnit'> & { periodUnit?: string };

interface StoreKitPlugin {
  getProducts(options: { productIds: string[] }): Promise<{
    canMakePayments: boolean;
    /** Missing in shells built before the field existed. */
    canManageSubscriptions?: boolean;
    products: NativeProduct[];
  }>;
  purchase(options: {
    productId: string;
    appAccountToken: string;
  }): Promise<{ status: string; transaction?: StoreKitTransaction }>;
  finishTransaction(options: { transactionId: string }): Promise<void>;
  currentEntitlements(): Promise<{ transactions: StoreKitTransaction[] }>;
  restore(): Promise<{ transactions: StoreKitTransaction[] }>;
  manageSubscriptions(): Promise<void>;
  addListener(
    eventName: 'transactionUpdated',
    listener: (transaction: StoreKitTransaction) => void
  ): Promise<PluginListenerHandle>;
}

// Static import of @capacitor/core for the reason given in ./auth.ts: a
// dynamic import of it never settled inside the packaged WKWebView. The plugin
// is registered on first use, never on the web.
let storeKit: StoreKitPlugin | null = null;

function getStoreKit(): StoreKitPlugin {
  if (!isStoreKitAvailable()) {
    throw new CapacitorException('StoreKit is not available here', ExceptionCode.Unavailable);
  }
  if (!storeKit) {
    storeKit = registerPlugin<StoreKitPlugin>('StoreKit');
  }
  return storeKit;
}

function toProduct(native: NativeProduct): StoreKitProduct {
  const product: StoreKitProduct = {
    id: native.id,
    displayName: native.displayName,
    description: native.description,
    displayPrice: native.displayPrice,
    price: native.price,
    currency: native.currency,
  };
  const unit = PERIOD_UNITS.find((candidate) => candidate === native.periodUnit?.toLowerCase());
  if (unit && typeof native.periodValue === 'number') {
    product.periodUnit = unit;
    product.periodValue = native.periodValue;
  }
  return product;
}

/** True in the iOS app once its shell carries the StoreKit plugin. */
export function isStoreKitAvailable(): boolean {
  return getNativePlatform() === 'ios' && Capacitor.isPluginAvailable('StoreKit');
}

/**
 * Loads the products from the App Store. IDs Apple does not know are left out,
 * and the order follows Apple, not `productIds`. `canMakePayments` is false
 * when purchases are disabled on the device (Screen Time); offer no buy button then.
 * `canManageSubscriptions` is false where Apple's manage sheet is not supported
 * (the iPhone app on a Mac with Apple silicon) and in shells that do not report
 * it yet; offer no manage button then, `manageSubscriptions()` would only fail.
 */
export async function getProducts(productIds: string[]): Promise<{
  canMakePayments: boolean;
  canManageSubscriptions: boolean;
  products: StoreKitProduct[];
}> {
  const result = await getStoreKit().getProducts({ productIds });
  return {
    canMakePayments: result.canMakePayments,
    canManageSubscriptions: result.canManageSubscriptions === true,
    products: result.products.map(toProduct),
  };
}

/** Opens Apple's payment sheet. `appAccountToken` is the one the API issued. */
export async function purchase(
  productId: string,
  appAccountToken: string
): Promise<StoreKitPurchaseOutcome> {
  const result = await getStoreKit().purchase({ productId, appAccountToken });
  if (result.status === 'success' && result.transaction) {
    return { status: 'success', transaction: result.transaction };
  }
  if (result.status === 'cancelled' || result.status === 'pending') {
    return { status: result.status };
  }
  throw new Error(`Unexpected StoreKit purchase result: ${result.status}`);
}

/** Resolves as well when the transaction is no longer open. */
export async function finishTransaction(transactionId: string): Promise<void> {
  await getStoreKit().finishTransaction({ transactionId });
}

export async function currentEntitlements(): Promise<StoreKitTransaction[]> {
  const result = await getStoreKit().currentEntitlements();
  return result.transactions;
}

/** Restore button only: Apple asks for the Apple Account password first. */
export async function restorePurchases(): Promise<StoreKitTransaction[]> {
  const result = await getStoreKit().restore();
  return result.transactions;
}

/**
 * Apple's own sheet to change or cancel the subscription. Rejects with
 * `UNAVAILABLE` where `getProducts()` reports `canManageSubscriptions: false`.
 */
export async function manageSubscriptions(): Promise<void> {
  await getStoreKit().manageSubscriptions();
}

/** Resolves to the function that removes the listener again. */
export async function onTransactionUpdated(
  listener: (tx: StoreKitTransaction) => void
): Promise<() => void> {
  const handle = await getStoreKit().addListener('transactionUpdated', listener);
  return () => {
    void handle.remove();
  };
}
